import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import moment from "moment";
import { apiData } from "../services/api_service";
import { stockTransferDataAPI, updateStockBatchPriceAPI } from "../config";
import { formatNumber } from "../utils/format";

// One-click price correction, opened straight from the Stock Report — same
// batch list + inline edit UI as Stock Adjustment's "Check Expiry" dialog
// (see InventoryManagement.jsx), just reachable without first re-selecting
// the product/store there. Both entry points call the same backend
// endpoint (update-batch-price), so either place is safe to use.
const formatDate = (dateStr) => {
  const d = moment(dateStr);
  return d.isValid() ? d.format("DD-MM-YYYY") : dateStr || "—";
};

const BatchPriceEditModal = ({
  open,
  onClose,
  productId,
  storeId,
  productName,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editBuyingPrice, setEditBuyingPrice] = useState("");
  const [editSellingPrice, setEditSellingPrice] = useState("");
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const fetchBatches = async () => {
    if (!productId || !storeId) return;
    try {
      setLoading(true);
      const result = await apiData(
        "get",
        `${stockTransferDataAPI}?product_id=${productId}&store_id=${storeId}`
      );
      setRows(result.success && Array.isArray(result.data) ? result.data : []);
    } catch (e) {
      console.error("Failed to fetch stock batches:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      // Reset edit state whenever the dialog closes, so reopening it for a
      // different product never shows a stale row mid-edit.
      setEditingBatchId(null);
      setEditBuyingPrice("");
      setEditSellingPrice("");
      return;
    }
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId, storeId]);

  const startEdit = (row) => {
    setEditingBatchId(row.live_stock_id);
    setEditBuyingPrice(row.buying_price ?? "");
    setEditSellingPrice(row.selling_price ?? "");
  };

  const cancelEdit = () => {
    setEditingBatchId(null);
    setEditBuyingPrice("");
    setEditSellingPrice("");
  };

  const saveEdit = async () => {
    if (editBuyingPrice === "" || editSellingPrice === "") {
      setSnackbar({ open: true, message: "Buying price and selling price are required", severity: "error" });
      return;
    }
    if (Number(editSellingPrice) < Number(editBuyingPrice)) {
      setSnackbar({ open: true, message: "Selling price cannot be lesser than buying price", severity: "error" });
      return;
    }
    try {
      setSaving(true);
      const result = await apiData("post", updateStockBatchPriceAPI, {
        live_stock_id: editingBatchId,
        buying_price: Number(editBuyingPrice),
        selling_price: Number(editSellingPrice),
        user_id: user.emp_id,
      });
      if (result.success) {
        setSnackbar({ open: true, message: result.message || "Price updated successfully", severity: "success" });
        cancelEdit();
        fetchBatches();
      } else {
        setSnackbar({ open: true, message: result.message || "Failed to update price", severity: "error" });
      }
    } catch (e) {
      console.error("Failed to update stock batch price:", e);
      setSnackbar({ open: true, message: "Something went wrong. Please try again.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Stock Price
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {productName || "Product"}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
              No stock batches found for this product at this store.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Manufacture Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expiry Days</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Buying Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Selling Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Stock Count</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const isEditing = editingBatchId === row.live_stock_id;
                    return (
                      <TableRow key={row.live_stock_id}>
                        <TableCell>{formatDate(row.manufacture_date)}</TableCell>
                        <TableCell>{row.life_days}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={editBuyingPrice}
                              onChange={(e) => setEditBuyingPrice(e.target.value)}
                              sx={{ width: 100 }}
                            />
                          ) : (
                            row.buying_price
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={editSellingPrice}
                              onChange={(e) => setEditSellingPrice(e.target.value)}
                              sx={{ width: 100 }}
                            />
                          ) : (
                            row.selling_price
                          )}
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.stock_count)}</TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <>
                              <IconButton size="small" color="primary" disabled={saving} onClick={saveEdit}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" disabled={saving} onClick={cancelEdit}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <IconButton size="small" title="Edit price" onClick={() => startEdit(row)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BatchPriceEditModal;
